import { ApplicationResult, Job } from '@cibli/shared';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { emitDataChanged } from '@/lib/dataEvents';
import { applyToJob as applyToJobApi } from '@/services/job';

type UseApplyToJobOptions = {
  cvId?: string | null;
  resolveCvId?: () => Promise<string | null>;
  requireCvSelection?: boolean;
  onSuccess?: () => void;
};

async function resolveFinalCvId(
  selectedCvId: string | undefined,
  cvId: string | null | undefined,
  resolveCvId: (() => Promise<string | null>) | undefined,
  requireCvSelection: boolean,
  setSelectCVDialogOpen: (open: boolean) => void,
  t: (key: string) => string,
): Promise<string | null> {
  let finalCvId = selectedCvId ?? cvId;

  // If no CV provided and requireCvSelection is true, open selection dialog
  if (!finalCvId && requireCvSelection) {
    setSelectCVDialogOpen(true);
    return null;
  }

  // If no CV provided and resolveCvId is available, try to resolve
  if (!finalCvId && resolveCvId) {
    try {
      finalCvId = await resolveCvId();
    } catch (error) {
      toast.error(t('errors.internal_error'));
      console.error(error);
      return null;
    }
  }

  // If still no CV, show error
  if (!finalCvId) {
    toast.error(t('dashboard.cvList.empty.title'));
    return null;
  }

  return finalCvId;
}

export function useApplyToJob({
  cvId,
  resolveCvId,
  requireCvSelection = false,
  onSuccess,
}: UseApplyToJobOptions = {}) {
  const { t } = useTranslation();
  const [isApplying, setIsApplying] = useState(false);
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);
  const [selectCVDialogOpen, setSelectCVDialogOpen] = useState(false);
  const [applicationResult, setApplicationResult] = useState<ApplicationResult | null>(null);

  const handleApplyToJob = useCallback(
    async (job: Job, selectedCvId?: string) => {
      const finalCvId = await resolveFinalCvId(
        selectedCvId,
        cvId,
        resolveCvId,
        requireCvSelection,
        setSelectCVDialogOpen,
        t,
      );

      if (!finalCvId) return;

      try {
        setIsApplying(true);
        const result = await applyToJobApi(job, finalCvId);
        setApplicationResult(result);
        setConfirmApplyOpen(true);

        // Notify other components that applications have changed
        emitDataChanged('applications-changed');

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        if (error instanceof Error && error.message === 'ALREADY_APPLIED') {
          toast.error(t('dashboard.jobs.errors.alreadyApplied'));
        } else {
          toast.error(t('errors.internal_error'));
        }
        console.error(error);
      } finally {
        setIsApplying(false);
      }
    },
    [cvId, resolveCvId, requireCvSelection, t, onSuccess],
  );

  return {
    applyToJob: handleApplyToJob,
    isApplying,
    confirmApplyOpen,
    setConfirmApplyOpen,
    selectCVDialogOpen,
    setSelectCVDialogOpen,
    applicationResult,
  };
}
